import { AxiosInstance } from 'axios';

const ACTIVE_GROUP_KEY = 'greenfirefly_active_group';
const ACTIVE_ORG_KEY = 'greenfirefly_active_org';
const ACTIVE_TEAM_KEY = 'greenfirefly_active_team';

/**
 * Get the active group ID from localStorage.
 */
export function getActiveGroupId(): string | null {
  return localStorage.getItem(ACTIVE_GROUP_KEY);
}

/**
 * Get the active organization ID from localStorage.
 */
export function getActiveOrganizationId(): string | null {
  return localStorage.getItem(ACTIVE_ORG_KEY);
}

/**
 * Get the active team ID from localStorage.
 */
export function getActiveTeamId(): string | null {
  return localStorage.getItem(ACTIVE_TEAM_KEY) || localStorage.getItem(ACTIVE_GROUP_KEY);
}

/**
 * Configure an axios instance for authenticated requests.
 *
 * Adds:
 * - withCredentials: true (for httpOnly cookies)
 * - X-Organization-Id header with active organization
 * - X-Team-Id header with active team
 * - X-Group-Id header with active group (backwards compatibility)
 * - 401 redirect to login
 * - 403 error handling
 */
export function configureAuthInterceptors(api: AxiosInstance): void {
  // Enable credentials for all requests (sends cookies)
  api.defaults.withCredentials = true;

  // Add request interceptor to include organization/team/group ID headers
  api.interceptors.request.use(
    (config) => {
      const orgId = getActiveOrganizationId();
      if (orgId) {
        config.headers['X-Organization-Id'] = orgId;
      }

      const teamId = getActiveTeamId();
      if (teamId) {
        config.headers['X-Team-Id'] = teamId;
      }

      // Backwards compatibility
      const groupId = getActiveGroupId();
      if (groupId) {
        config.headers['X-Group-Id'] = groupId;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor for auth errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Unauthorized - redirect to login
        // Only redirect if we're not already on auth pages
        const currentPath = window.location.pathname;
        if (!currentPath.startsWith('/login') && !currentPath.startsWith('/onboarding')) {
          // Preserve current URL for post-login redirect
          const nextUrl = currentPath + window.location.search;
          window.location.href = '/login?next=' + encodeURIComponent(nextUrl);
        }
      }
      // 403 errors are passed through - components can show permission denied
      return Promise.reject(error);
    }
  );
}

/**
 * Create headers object with auth info for fetch requests.
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const orgId = getActiveOrganizationId();
  if (orgId) {
    headers['X-Organization-Id'] = orgId;
  }

  const teamId = getActiveTeamId();
  if (teamId) {
    headers['X-Team-Id'] = teamId;
  }

  // Backwards compatibility
  const groupId = getActiveGroupId();
  if (groupId) {
    headers['X-Group-Id'] = groupId;
  }
  return headers;
}
